import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  inject,
  signal,
  computed,
  ViewChild,
  ElementRef,
  AfterViewInit,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { BpmnModelerService } from '../services/bpmn-modeler.service';
import { BpmnLintResult, BpmnLintService } from '../services/bpmn-lint.service';
import { BpmnService } from '../bpmn-list/services/bpmn.service';
import { BpmnItem } from '../bpmn-list/bpmn.config';
import { AppButtonComponent } from '@app/shared/components/app-button/app-button.component';
import { TextNormalizerService } from '@shared/services/text-normalizer.service';
import { vietnameseTextPatternValidator } from '../../../center-business/goods/classify/models/classify.config';
import { CanComponentDeactivate } from '@app/core/auth/can-component-deactivate.interface';

@Component({
  selector: 'app-bpmn-edit',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    InputTextModule,
    ToastModule,
    TranslateModule,
    ReactiveFormsModule,
    AppButtonComponent,
  ],
  templateUrl: './bpmn-edit.component.html',
  styleUrl: './bpmn-edit.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BpmnEditComponent
  implements OnInit, OnDestroy, AfterViewInit, CanComponentDeactivate
{
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly translateService = inject(TranslateService);
  private readonly bpmnModelerService = inject(BpmnModelerService);
  private readonly bpmnService = inject(BpmnService);
  private readonly messageService = inject(MessageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);
  private readonly destroy$ = new Subject<void>();
  private readonly bpmnLintService = inject(BpmnLintService);
  private readonly textNormalizer = inject(TextNormalizerService);
  private skipCanDeactivate = false;

  // Reactive form for process information
  processForm!: FormGroup;

  @ViewChild('modelerContainer', { static: false })
  modelerContainer!: ElementRef;

  @ViewChild('propertiesPanelContainer', { static: false })
  propertiesPanelContainer!: ElementRef;

  // Signals for component state
  loading = signal(false);
  hasUnsavedChanges = signal(false);
  processData = signal<BpmnItem | null>(null);
  lintResults = signal<BpmnLintResult>({
    issues: [],
    hasErrors: false,
    hasWarnings: false,
    hasInfo: false,
  });

  // Computed properties
  isSaveDisabled = computed(() => {
    return this.loading();
  });

  hasLintErrors = computed(() => {
    return this.lintResults().hasErrors;
  });

  hasLintWarnings = computed(() => {
    return this.lintResults().hasWarnings;
  });

  hasLintIssues = computed(() => {
    const results = this.lintResults();
    return results.hasErrors || results.hasWarnings || results.hasInfo;
  });

  lintErrorCount = computed(() => {
    return this.lintResults().issues.filter(
      (issue) => issue.severity === 'error',
    ).length;
  });

  lintWarningCount = computed(() => {
    return this.lintResults().issues.filter(
      (issue) => issue.severity === 'warn',
    ).length;
  });

  lintInfoCount = computed(() => {
    return this.lintResults().issues.filter(
      (issue) => issue.severity === 'info',
    ).length;
  });

  ngOnInit(): void {
    // Initialize reactive form
    this.processForm = this.fb.group({
      processName: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(100),
        ],
      ],
      processDefineId: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(50),
          vietnameseTextPatternValidator(
            /^[a-zA-Z0-9_-]+$/,
            this.textNormalizer,
          ),
        ],
      ],
    });

    // Track form changes for unsaved changes detection
    this.processForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.hasUnsavedChanges.set(true);
        this.cdr.markForCheck();
      });

    const processId = this.route.snapshot.paramMap.get('id');
    if (processId) {
      this.loadProcessData(processId);
    } else {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant(
          'bpmn.edit.messages.error.title',
        ),
        detail: this.translateService.instant(
          'bpmn.edit.messages.error.invalid_id',
        ),
      });
    }
  }

  ngAfterViewInit(): void {
    // Initialize BPMN modeler after view is ready
    this.initializeBpmnModeler();
    this.setupBpmnChangeListener();
    this.setupLintingListener();
  }

  /**
   * Get form control for easier access in template
   */
  getFormControl(controlName: string) {
    return this.processForm.get(controlName);
  }

  /**
   * Check if a form field has an error
   */
  hasFieldError(fieldName: string): boolean {
    const control = this.getFormControl(fieldName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  /**
   * Get error message for a form field
   */
  getFieldErrorMessage(fieldName: string): string {
    const control = this.getFormControl(fieldName);
    if (!control?.errors) return '';

    // Map field names to translation key format
    const fieldKeyMap: { [key: string]: string } = {
      processName: 'process_name',
      processDefineId: 'process_define_id',
    };
    const fieldKey = fieldKeyMap[fieldName] || fieldName;

    if (control.errors['required']) {
      return this.translateService.instant(
        `bpmn.create.form.validation.${fieldKey}_required`,
      );
    }
    if (control.errors['minlength']) {
      return this.translateService.instant(
        `bpmn.create.form.validation.${fieldKey}_min_length`,
      );
    }
    if (control.errors['maxlength']) {
      return this.translateService.instant('common.validation.maxlength', {
        fieldName: this.translateService.instant(`bpmn.create.form.${fieldKey}`),
        requiredLength: control.errors['maxlength'].requiredLength,
      });
    }
    if (control.errors['pattern']) {
      return this.translateService.instant('common.validation.pattern');
    }

    return '';
  }

  /**
   * Load process data by ID
   */
  private loadProcessData(processId: string): void {
    this.loading.set(true);

    // Get process data and XML content in a single API call
    this.bpmnService.getBpmnProcessWithXml(processId).subscribe({
      next: (details) => {
        if (details?.process) {
          this.processData.set(details.process);

          // Populate form with existing data
          this.processForm.patchValue({
            processName: details.process.name || '',
            processDefineId:
              details.process.definitionId || details.process.id || '',
          });

          // Load BPMN XML content if available
          if (details.xmlContent) {
            this.importBpmnXml(details.xmlContent);
          } else {
            this.messageService.add({
              severity: 'warn',
              summary: this.translateService.instant(
                'bpmn.edit.messages.warning.title',
              ),
              detail: this.translateService.instant(
                'bpmn.edit.messages.warning.no_xml_content',
              ),
            });
          }
        } else {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant(
              'bpmn.edit.messages.error.title',
            ),
            detail: this.translateService.instant(
              'bpmn.edit.messages.error.process_not_found',
            ),
          });
        }
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Failed to load process data:', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant(
            'bpmn.edit.messages.error.title',
          ),
          detail: this.translateService.instant(
            'bpmn.edit.messages.error.failed_to_load',
          ),
        });
        this.loading.set(false);
      },
    });
  }

  /**
   * Import BPMN XML content into the modeler
   */
  private async importBpmnXml(xmlContent: string): Promise<void> {
    try {
      await this.bpmnModelerService.importXml(xmlContent);
    } catch (error) {
      console.error('Failed to import BPMN XML:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant(
          'bpmn.edit.messages.error.title',
        ),
        detail: this.translateService.instant(
          'bpmn.edit.messages.error.failed_to_load_content',
        ),
      });
    }
  }

  /**
   * Check if BPMN modeler has content (more than just an empty process)
   */
  private hasBpmnContent(): boolean {
    try {
      const modeler = this.bpmnModelerService.getModeler();
      if (!modeler) return false;

      const elementRegistry = modeler.get('elementRegistry');
      const elements = elementRegistry.getAll();

      // Check if there are any meaningful BPMN elements
      const meaningfulElements = elements.filter((element: any) => {
        const type = element.type;
        // Exclude structural elements and include only process elements
        return (
          type &&
          !type.includes('bpmn:Process') &&
          !type.includes('bpmn:Collaboration') &&
          !type.includes('bpmn:Participant') &&
          !type.includes('bpmn:BPMNDiagram') &&
          !type.includes('bpmn:BPMNPlane') &&
          !type.includes('bpmn:BPMNShape') &&
          !type.includes('bpmn:BPMNEdge')
        );
      });

      return meaningfulElements.length > 0;
    } catch (error) {
      console.warn('Error checking BPMN content:', error);
      return false;
    }
  }

  /**
   * Initialize BPMN modeler
   */
  private async initializeBpmnModeler(): Promise<void> {
    try {
      if (
        this.modelerContainer?.nativeElement &&
        this.propertiesPanelContainer?.nativeElement
      ) {
        await this.bpmnModelerService.initialize({
          container: this.modelerContainer.nativeElement,
          propertiesPanel: this.propertiesPanelContainer.nativeElement,
          width: '100%',
          height: '100%',
        });
      }
    } catch (error) {
      console.error('Failed to initialize BPMN modeler:', error);
    }
  }

  /**
   * Setup listener for BPMN linting results
   */
  private setupLintingListener(): void {
    this.bpmnModelerService
      .getLintResults$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((results: BpmnLintResult) => {
        this.lintResults.set(results);
        this.cdr.markForCheck();
      });
  }

  /**
   * Setup listener for BPMN changes to update save button state
   */
  private setupBpmnChangeListener(): void {
    try {
      const modeler = this.bpmnModelerService.getModeler();
      if (!modeler) return;

      // Listen for element changes
      const eventBus = modeler.get('eventBus');
      const commandStack = modeler.get('commandStack');

      // Use command stack changes for more reliable change detection
      if (commandStack && typeof commandStack.on === 'function') {
        commandStack.on('changed', () => {
          this.hasUnsavedChanges.set(true);
          this.cdr.markForCheck();
        });
      }

      // Also listen to specific element events as backup
      eventBus.on('element.changed', () => {
        this.hasUnsavedChanges.set(true);
        this.cdr.markForCheck();
      });

      eventBus.on('element.added', () => {
        this.hasUnsavedChanges.set(true);
        this.cdr.markForCheck();
      });

      eventBus.on('element.removed', () => {
        this.hasUnsavedChanges.set(true);
        this.cdr.markForCheck();
      });
    } catch (error) {
      console.warn('Failed to setup BPMN change listener:', error);
    }
  }

  /**
   * Clear all BPMN content
   */
  async onClearAll(): Promise<void> {
    try {
      // Clear BPMN modeler - this will reset to empty process
      await this.bpmnModelerService.clear();
    } catch (error) {
      console.error('Failed to clear BPMN modeler:', error);
    }
  }

  /**
   * Zoom in the BPMN canvas
   */
  onZoomIn(): void {
    this.bpmnModelerService.zoomIn();
  }

  /**
   * Zoom out the BPMN canvas
   */
  onZoomOut(): void {
    this.bpmnModelerService.zoomOut();
  }

  /**
   * Fit the BPMN diagram to viewport
   */
  onFitToViewport(): void {
    this.bpmnModelerService.fitToViewport();
  }

  /**
   * Reset zoom to 100%
   */
  onResetZoom(): void {
    this.bpmnModelerService.resetZoom();
  }

  /**
   * Import BPMN file
   */
  async onImportFile(): Promise<void> {
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.bpmn,.xml';
      input.onchange = async (event: any) => {
        const file = event.target.files[0];
        if (file) {
          const text = await file.text();
          await this.bpmnModelerService.importXml(text);
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant(
              'bpmn.edit.messages.import_success',
            ),
            detail: this.translateService.instant(
              'bpmn.edit.messages.import_success_detail',
            ),
          });
          this.hasUnsavedChanges.set(true);
          this.cdr.markForCheck();
        }
      };
      input.click();
    } catch (error) {
      console.error('Failed to import BPMN file:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant(
          'bpmn.edit.messages.import_failed',
        ),
        detail: this.translateService.instant(
          'bpmn.edit.messages.import_error_generic',
        ),
      });
    }
  }

  /**
   * Export BPMN as file
   */
  async exportBpmnAsFile(): Promise<void> {
    try {
      const result = await this.bpmnModelerService.exportXml();

      if (result.error) {
        console.error('Export failed:', result.error);
        return;
      }

      const processData = this.processData();
      // Use fileName from API response, fallback to name if not available
      const fileName =
        processData?.fileName || processData?.name || 'bpmn-process';
      const blob = new Blob([result.xml], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export BPMN file:', error);
    }
  }

  /**
   * Export BPMN as image
   */
  async exportBpmnAsImage(): Promise<void> {
    try {
      const result = await this.bpmnModelerService.exportSvg();

      if (result.error) {
        console.error('SVG export failed:', result.error);
        return;
      }

      const processData = this.processData();
      // Use fileName from API response, fallback to name if not available
      const baseFileName =
        processData?.fileName || processData?.name || 'bpmn-process';
      // Remove .bpmn extension if present and add .svg
      const fileName = baseFileName.replace(/\.bpmn$/, '') + '.svg';
      const blob = new Blob([result.svg], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export BPMN image:', error);
    }
  }

  /**
   * Handle Export File button click
   */
  onExportFile(): void {
    this.exportBpmnAsFile();
  }

  /**
   * Handle Export Image button click
   */
  onExportImage(): void {
    this.exportBpmnAsImage();
  }

  ngOnDestroy(): void {
    this.bpmnModelerService.destroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  canDeactivate(): boolean {
    if (this.skipCanDeactivate || this.loading()) {
      return true;
    }

    return !(this.processForm?.dirty || this.hasUnsavedChanges());
  }

  /**
   * Handle Cancel button click
   */
  onCancel(): void {
    if (this.hasUnsavedChanges()) {
      // TODO: Show confirmation dialog if there are unsaved changes
      const confirmed = confirm(
        this.translateService.instant('bpmn.edit.unsaved_changes_confirmation'),
      );
      if (!confirmed) {
        return;
      }
    }
    this.router.navigate(['/configuration/master-data/bpmn']);
  }

  /**
   * Handle Save button click
   */
  async onSave(): Promise<void> {
    // Validate form
    if (this.processForm.invalid) {
      this.processForm.markAllAsTouched();
      return;
    }

    // Check for linting errors before saving
    const lintResults = this.bpmnLintService.getCurrentLintResults();

    // Check if there are any errors
    if (lintResults.hasErrors) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant(
          'bpmn.create.messages.validation_failed',
        ),
        detail: this.translateService.instant(
          'bpmn.create.messages.linting_errors_present',
        ),
      });
      return;
    }

    if (this.isSaveDisabled()) {
      return;
    }

    this.loading.set(true);

    try {
      // Export BPMN XML
      const xmlResult = await this.bpmnModelerService.exportXml();
      if (xmlResult.error) {
        throw new Error(xmlResult.error);
      }

      // Get form values and normalize Vietnamese text before submission
      const formValues = this.processForm.value;

      // Call the real API to update the process
      const processData = this.processData();
      if (!processData?.id) {
        throw new Error('Process ID is required for update');
      }

      // Normalize Vietnamese text before submission
      const normalizedProcessName = this.textNormalizer.normalizeToNFC(
        formValues.processName || '',
      );
      const normalizedProcessDefineId = this.textNormalizer.normalizeToNFC(
        formValues.processDefineId || '',
      );

      this.bpmnService
        .updateBpmnProcess(
          processData.id,
          normalizedProcessName,
          normalizedProcessDefineId,
          xmlResult.xml,
        )
        .subscribe({
          next: (success) => {
            this.loading.set(false);
            if (success) {
              this.skipCanDeactivate = true;
              this.processForm.markAsPristine();
              this.hasUnsavedChanges.set(false);
              this.messageService.add({
                severity: 'success',
                summary: this.translateService.instant(
                  'bpmn.edit.messages.save_success',
                ),
                detail: this.translateService.instant(
                  'bpmn.edit.messages.save_success_detail',
                ),
              });

              // Navigate back to the list page after successful save
              setTimeout(() => {
                this.router.navigate(['/configuration/master-data/bpmn']);
              }, 1500); // Wait 1.5 seconds to show the success message
            } else {
              this.messageService.add({
                severity: 'error',
                summary: this.translateService.instant(
                  'bpmn.edit.messages.save_failed',
                ),
                detail: this.translateService.instant(
                  'bpmn.edit.messages.save_failed_detail',
                ),
              });
            }
          },
          error: (error) => {
            console.error('Error saving BPMN process:', error);
            this.loading.set(false);
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant(
                'bpmn.edit.messages.save_failed',
              ),
              detail: String(error),
            });
          },
        });
    } catch (error) {
      console.error('Error saving BPMN process:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant(
          'bpmn.edit.messages.save_failed',
        ),
        detail: String(error),
      });
      this.loading.set(false);
    }
  }
}
